import { useEffect, useMemo, useState } from 'react';
import type { Question } from '../../types';
import type { TestResult as TestResultType } from '../../types/test';
import type { QuestionAggregation } from '../../stores/lectureStore';
import { TestQuestion } from './TestQuestion';
import { stripHtmlTags } from '../../utils/questionTextUtils';
import { isCorrectAnswer } from '../../utils/answerJudge';

export interface TestResultListProps {
  questions: Question[];
  selectedChoices: Record<number, number | null>;
  questionAggregations?: Record<number, QuestionAggregation>;
  isArchive?: boolean;
  userId?: string;
  lectureId?: string;
}

type ViewMode = 'list' | 'detail' | 'review';

/**
 * TestResultList - テスト結果のナビゲーション
 * 一覧表示と、サマリー確認・各問題クリックで解説中画面（各問題の解説）を表示
 */
export function TestResultList({
  questions,
  selectedChoices,
  questionAggregations = {},
  isArchive = false,
  userId,
  lectureId,
}: TestResultListProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [isSp, setIsSp] = useState(() => window.matchMedia('(max-width: 1023px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handleChange = () => setIsSp(mq.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  // 問題をソート
  const sortedQuestions = useMemo(
    () => [...questions].sort((a, b) => a.sort_key - b.sort_key),
    [questions],
  );

  // 各問題のステータスを取得
  const getQuestionStatus = (question: Question): string => {
    const selectedChoiceId = selectedChoices[question.id];
    
    // デバッグ: question.id と selectedChoices の内容を確認
    console.log('[TestResultList] getQuestionStatus:', {
      questionId: question.id,
      questionSortKey: question.sort_key,
      formatType: question.format_type,
      selectedChoiceId,
      selectedChoicesKeys: Object.keys(selectedChoices),
      selectedChoices,
    });
    
    if (selectedChoiceId === undefined) {
      return '未実施';
    }
    // アンケート形式の場合は正解・不正解を表示しない
    if (question.format_type === 'survey') {
      if (selectedChoiceId === null) {
        return '未解答';
      }
      return '解答済'; // 解答済みの場合は「解答済」ラベルを表示
    }
    if (selectedChoiceId === null) {
      return '未解答';
    }
    // 正誤判定はバックエンド（最初の is_correct のみ正解）と定義を統一（AGG-10対応）
    if (isCorrectAnswer(question, selectedChoiceId)) {
      return '正解';
    }
    return '不正解';
  };

  const currentQuestion = sortedQuestions[currentIndex];
  const totalQuestions = sortedQuestions.length;

  const handlePrev = () => {
    if (viewMode === 'list') {
      // 一覧表示の場合は、最後の問題の詳細を表示
      setCurrentIndex(totalQuestions - 1);
      setViewMode('detail');
    } else {
      // 詳細表示の場合は、前の問題に移動
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    }
  };

  const handleNext = () => {
    if (viewMode === 'list') {
      // 一覧表示の場合は、最初の問題（1番目）の詳細を表示
      setCurrentIndex(0);
      setViewMode('detail');
    } else {
      // 詳細表示の場合は、次の問題に移動
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }
  };

  /** questionAggregations から TestResult[] 形式に変換 */
  const getResultsForQuestion = (question: Question): TestResultType[] => {
    const aggregation = questionAggregations[question.id];
    if (!aggregation) return [];
    return question.choices.map((c, idx) => {
      const key = `choice_${idx + 1}`;
      const count = aggregation.choiceCounts[key] || 0;
      return {
        choiceId: c.id,
        count,
        percentage: aggregation.totalAnswers > 0
          ? (count / aggregation.totalAnswers) * 100
          : 0,
      };
    });
  };

  const handleBackToList = () => {
    setViewMode('list');
    setCurrentIndex(0);
  };

  const handleShowReview = () => {
    setViewMode('review');
  };

  const handleOpenDetail = (index: number) => {
    setCurrentIndex(index);
    setViewMode('detail');
  };

  const renderQuestionExplanation = (question: Question) => (
    <TestQuestion
      question={question}
      displayNumber={question.display_number ?? undefined}
      selectedChoiceId={selectedChoices[question.id] ?? null}
      isSubmitted={true}
      isExplaining={true}
      isArchive={isArchive}
      archiveSelectedChoiceId={selectedChoices[question.id] ?? undefined}
      results={getResultsForQuestion(question)}
      onSelectChoice={() => {}}
      onSubmit={() => {}}
      userId={userId}
      lectureId={lectureId}
    />
  );

  /** 上下共通のナビゲーションバー（SP: ← / 一覧に戻る / →） */
  const renderNav = (position: 'top' | 'bottom') => (
    <div className={`test-result-list-nav test-result-list-nav-${position}`}>
      <button
        type="button"
        className="test-archive-nav-btn"
        data-archive-nav="prev"
        aria-label="戻る"
        onClick={handlePrev}
        disabled={viewMode === 'review' || (viewMode === 'detail' && currentIndex === 0)}
      >
        {isSp ? '←' : '← 戻る'}
      </button>
      <button
        type="button"
        className="test-archive-nav-btn"
        data-archive-nav="list"
        aria-label="テスト結果一覧に戻る"
        onClick={handleBackToList}
        disabled={viewMode === 'list'}
      >
        {isSp ? '一覧に戻る' : 'テスト結果一覧に戻る'}
      </button>
      <button
        type="button"
        className="test-archive-nav-btn"
        data-archive-nav="next"
        aria-label="進む"
        onClick={handleNext}
        disabled={viewMode === 'review' || (viewMode === 'detail' && currentIndex >= totalQuestions - 1)}
      >
        {isSp ? '→' : '進む →'}
      </button>
    </div>
  );

  return (
    <div className="test-archive-shell">
      {/* ヘッダー（常時表示） */}
      <div className="test-archive-header-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '6px' }}>
          <div className="test-archive-header-title">テスト結果</div>
          {!isArchive && (
            <button
              type="button"
              className="test-summary-survey-cta"
              onClick={handleShowReview}
              disabled={viewMode === 'review'}
            >
              サマリー確認
            </button>
          )}
        </div>
        {renderNav('top')}
      </div>

      {/* コンテンツ（一覧 / 解説1問 / 全問縦一覧） */}
      {viewMode === 'review' ? (
        <div className="test-review-list" aria-label="テスト結果一覧">
          {sortedQuestions.map((question) => (
            <div key={question.id} className="test-review-item">
              {renderQuestionExplanation(question)}
            </div>
          ))}
        </div>
      ) : viewMode === 'detail' && currentQuestion ? (
        <div className="test-archive-detail">
          {renderQuestionExplanation(currentQuestion)}
        </div>
      ) : (
        <div className="test-archive-toc">
          <div className="test-archive-toc-list">
            {sortedQuestions.map((question, index) => {
              const status = getQuestionStatus(question);
              const isSelected = index === currentIndex;
              const questionLabel =
                question.format_type === 'survey'
                  ? 'アンケート'
                  : question.format_type === 'example'
                    ? '例題'
                    : question.format_type === 'question' && question.display_number != null
                      ? `問題${question.display_number}`
                      : '問題';

              return (
                <button
                  key={question.id}
                  type="button"
                  className={`test-archive-toc-item ${isSelected ? 'active' : ''}`}
                  onClick={() => handleOpenDetail(index)}
                >
                  <div className="test-archive-toc-item-main">
                    <div className="test-archive-toc-item-title">{questionLabel}</div>
                    <div className="test-archive-toc-item-question">
                      {stripHtmlTags(question.question_text)}
                    </div>
                  </div>
                  {status && (
                    <span
                      className={`test-archive-toc-status ${
                        status === '正解' || status === '解答済'
                          ? 'toc-status-correct'
                          : status === '不正解'
                            ? 'toc-status-incorrect'
                            : status === '未解答'
                              ? 'toc-status-muted'
                              : 'toc-status-neutral'
                      }`}
                    >
                      {status}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ボトムナビ（常時表示） */}
      <div className="test-archive-shell-bottom-nav">
        {renderNav('bottom')}
      </div>
    </div>
  );
}
